import React from 'react'

import { faBars, faBookmark, faCompass, faEnvelope, faHome, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Backdrop, Box, Divider, List, Drawer as MuiDrawer } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import PropTypes from 'prop-types'

import { useResponsiveBreakpoint } from 'common/use-responsive-breakpoint'
import { AuthShieldComponent, AuthShieldHide, useIsAuthed } from 'components/AuthShield'
import { FEATURE_NAMES } from 'components/ConfiguredDynamicFeatureProvider/features'
import DynamicFeature from 'components/DynamicFeatureProvider/DynamicFeature'
import ScrollWrapper from 'components/ScrollWrapper'

import StudentAwesomeBar from '../StudentAwesomeBar'
import CreateAccount from '../components/CreateAccount'
import LoginButton from '../components/LoginButton'
import LogoutButton from '../components/LogoutButton'
import PagesList from '../components/PagesList'
import PendingInvites from '../components/PendingInvites'
import ProfilePreview from '../components/ProfilePreview'
import SidebarListItem from '../components/SidebarListItem'
import ApplyWithScoir from './ApplyWithScoir'
import { useCloseOnChanges } from './use-close-on-changes'

const useStyles = makeStyles((theme) => ({
  paper: {
    zIndex: 10,
    position: 'fixed',
    minWidth: '250px',
    paddingTop: '64px',
    [theme.breakpoints.between(961, 1080)]: {
      paddingTop: 0,
      marginTop: '64px',
      height: 'calc(100% - 64px)',
    },
    [theme.breakpoints.down('xs')]: {
      paddingTop: '57px',
      width: '100%',
      height: '100%',
      marginTop: '0px',
    },
  },
  icon: {
    fontSize: '21px',
    marginBottom: '-10px',
  },
  iconButton: {
    height: '45px',
    width: '45px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

/**
 * Drawer version of sidebar for responsive designs
 * @param {object} props
 * @prop {bool} open
 * @prop {func} onClose
 * @returns Drawer Component
 */
const Drawer = ({ open, onClose, pathname, queryParams }) => {
  const classes = useStyles()
  const isXs = useResponsiveBreakpoint('xs')
  const theme = useTheme()
  useCloseOnChanges(open, onClose, [pathname, queryParams])
  const isAuthed = useIsAuthed()
  const x = useMediaQuery(theme.breakpoints.between(961, 1080))
  const hideAuthButtons = isAuthed || x
  const root = import.meta.env.BASE_URL

  return (
    <React.Fragment>
      <Backdrop
        open={open}
        onClick={onClose}
        invisible
      />
      <MuiDrawer
        open={open}
        variant="persistent"
        anchor="right"
        classes={{ paper: classes.paper }}
      >
        <ScrollWrapper
          maxHeight={`calc(100vh - 64px)`}
          stopPropagation
        >
          {isXs && (
            <React.Fragment>
              <Box m={3}>
                <StudentAwesomeBar />
              </Box>
              <Box mt={0.5}>
                <Divider />
              </Box>
            </React.Fragment>
          )}
          {!isAuthed ? null : (
            <React.Fragment>
              <ProfilePreview onSelectLink={onClose} />
              <PendingInvites />
            </React.Fragment>
          )}
          {hideAuthButtons ? null : (
            <React.Fragment>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                width="100%"
                p={2}
                pb={0}
              >
                <CreateAccount fullWidth />
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                width="100%"
                p={2}
              >
                <LoginButton fullWidth />
              </Box>
            </React.Fragment>
          )}
          <Divider />
          <List>
            <SidebarListItem
              id="main-nav-sidebar-home"
              label="Home"
              pathname={pathname}
              to={`${import.meta.env.BASE_URL}/dashboard`}
              icon={faHome}
            />
            <AuthShieldComponent
              id="main-nav-sidebar-discover"
              label="Discover"
              component={SidebarListItem}
              pathname={pathname}
              icon={faCompass}
              propsAuthed={{
                to: `${import.meta.env.BASE_URL}/discover`,
              }}
              propsUnauthed={{
                to: `${import.meta.env.BASE_URL}/discover/search`,
              }}
            />
            <DynamicFeature name={FEATURE_NAMES.MyColleges}>
              <SidebarListItem
                id="main-nav-sidebar-my-colleges"
                label="My Colleges"
                pathname={pathname}
                to={`${root}/my-colleges`}
                icon={faBookmark}
              />
            </DynamicFeature>
            <DynamicFeature name={FEATURE_NAMES.MyColleges}>
              <SidebarListItem
                id="main-nav-sidebar-outreach"
                label="Message Center"
                pathname={pathname}
                to={`${root}/message-center/outreach`}
                icon={faEnvelope}
              />
            </DynamicFeature>
            <AuthShieldHide hideUnauthed>
              <DynamicFeature name={FEATURE_NAMES.MyColleges}>
                <ApplyWithScoir />
              </DynamicFeature>
            </AuthShieldHide>
          </List>
          <Box mt={0.5}>
            <Divider />
          </Box>
          <PagesList pathname={pathname} />
          <Box mt={0.5}>
            <Divider />
          </Box>
          {!isAuthed ? null : <LogoutButton />}
        </ScrollWrapper>
      </MuiDrawer>
    </React.Fragment>
  )
}

Drawer.propTypes = {
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  pathname: PropTypes.string,
  queryParams: PropTypes.string,
}

export default Drawer

export const DrawerTrigger = ({ open, onOpen, onClose }) => {
  const classes = useStyles()
  return (
    <IconButton
      id="main-nav-drawer-sidebar-trigger"
      color="default"
      aria-label="Menu"
      className={classes.iconButton}
      onClick={open ? onClose : onOpen}
    >
      <FontAwesomeIcon icon={open ? faTimes : faBars} />
    </IconButton>
  )
}

DrawerTrigger.propTypes = Drawer.propTypes
